import type { ComponentProps } from '@enonic/react-components'
import type { BreadcrumbItem, ResponsiveImageData } from '../primitives'
import * as React from 'react'
import { Section, Container, ResponsiveImage } from '../primitives'

export interface NewsData extends Record<string, unknown> {
    title: string
    lead?: string
    image?: ResponsiveImageData
    publishedDate?: string
    formattedDate?: string
    category?: string
}

export const News = (props: ComponentProps) => {
    const data = props.data as NewsData
    const commonData = props.common as Record<string, unknown> | undefined
    const breadcrumbs = commonData?.breadcrumbs as BreadcrumbItem[] | undefined
    return (
        <Section>
            <Container className="gap-y-5 md:gap-y-8">
                {breadcrumbs && breadcrumbs.length > 1 && (
                    <nav aria-label="Brødsmuler" className="col-span-full md:col-span-10 md:col-start-2">
                        {/* Mobile: single back link to parent */}
                        <a
                            href={breadcrumbs[breadcrumbs.length - 2].url}
                            className="inline-flex items-center gap-1 font-sans text-base tracking-wide leading-snug text-slate-700 hover:underline focus-visible:outline-1 focus-visible:outline-slate-300 md:hidden"
                        >
                            <span aria-hidden="true">←</span>
                            {breadcrumbs[breadcrumbs.length - 2].title}
                        </a>
                        {/* Desktop: full breadcrumb */}
                        <ol className="hidden items-center gap-2 font-sans text-lg tracking-wide leading-snug md:flex">
                            {breadcrumbs.map((item, index) => {
                                const isLast = index === breadcrumbs.length - 1
                                return (
                                    <li key={index} className="flex items-center gap-2">
                                        {index > 0 && (
                                            <span className="text-slate-700" aria-hidden="true">/</span>
                                        )}
                                        {isLast ? (
                                            <span className="text-black" aria-current="page">
                                                {item.title}
                                            </span>
                                        ) : (
                                            <a
                                                href={item.url}
                                                className="text-slate-700 hover:underline focus-visible:outline-1 focus-visible:outline-slate-300"
                                            >
                                                {item.title}
                                            </a>
                                        )}
                                    </li>
                                )
                            })}
                        </ol>
                    </nav>
                )}
                {data.category && (
                    <div className="col-span-full md:col-span-10 md:col-start-2">
                        <span className="inline-flex items-center rounded bg-blue-300 px-4 py-1 text-xs md:text-sm text-black">
                            {data.category}
                        </span>
                    </div>
                )}
                <h1 className="col-span-full md:col-span-10 md:col-start-2 font-serif text-4xl leading-tightest text-blue-900 md:text-7xl">
                    {data.title}
                </h1>
                {data.lead && (
                    <p className="col-span-full md:col-span-8 md:col-start-2 text-xl md:text-3xl text-black">
                        {data.lead}
                    </p>
                )}
                {data.formattedDate && (
                    <div className="col-span-full md:col-span-10 md:col-start-2 flex gap-4 text-xs md:text-sm">
                        <time className="text-slate-900" dateTime={data.publishedDate}>
                            {data.formattedDate}
                        </time>
                    </div>
                )}
                {data.image && (
                    <figure
                        className={
                            data.image.height > data.image.width
                                ? 'col-span-full md:col-span-6 md:col-start-4 flex flex-col gap-3 md:gap-4'
                                : 'col-span-full md:col-span-10 md:col-start-2 flex flex-col gap-3 md:gap-4'
                        }
                    >
                        <ResponsiveImage
                            data={data.image}
                            fetchpriority="high"
                            className="w-full object-cover"
                        />
                        {(data.image.caption || data.image.artist) && (
                            <figcaption className="text-xs md:text-sm text-slate-900">
                                {data.image.caption && <p>{data.image.caption}</p>}
                                {data.image.artist && <p>Foto: {data.image.artist}</p>}
                            </figcaption>
                        )}
                    </figure>
                )}
            </Container>
        </Section>
    )
}
